import { authHeader } from "../_helpers";
import { authenticatedIdentity, recoverSession } from './webSession';
export { authenticatedIdentity, authRecovery } from './webSession';

/**
 * Attach the active authorization and recover only the identity that sent it.
 */
export const apiFetch = async (url, options = {}) => {
    const { expectedIdentity, ...requestOptions } = options;
    const requestIdentity = expectedIdentity === undefined ? authenticatedIdentity() : expectedIdentity;
    if (requestIdentity !== authenticatedIdentity()) throw new Error('Account changed.');
    const headers = { ...(requestOptions.headers || {}), ...authHeader() };
    const res = await fetch(url, { ...requestOptions, headers });
    
    if (res.status === 401) recoverSession(requestIdentity, 'Your session expired. Please sign in again.');
    
    return res;
};

const abortError = () => Object.assign(new Error("The request was aborted."), { name: "AbortError" });

/**
 * Shares one request between identical callers while keeping each caller's
 * AbortSignal independent. The transport is cancelled only after its final
 * consumer leaves.
 */
export const createMultiConsumerGet = (request) => {
    const inFlight = new Map();
    const settle = (key, entry, complete) => {
        if (inFlight.get(key) === entry) inFlight.delete(key);
        const consumers = [...entry.consumers];
        entry.consumers.clear();
        consumers.forEach((consumer) => {
            consumer.signal?.removeEventListener("abort", consumer.release);
            complete(consumer);
        });
    };

    return (key, options = {}) => {
        const result = new Promise((resolve, reject) => {
        let entry = inFlight.get(key);
        if (!entry) {
            const controller = new AbortController();
            entry = { controller, consumers: new Set() };
            inFlight.set(key, entry);
            Promise.resolve(request(key, { ...options, signal: controller.signal }))
                .then(
                    (value) => settle(key, entry, (consumer) => consumer.resolve(value)),
                    (error) => settle(key, entry, (consumer) => consumer.reject(error))
                );
        }

        let active = true;
        const consumer = { resolve, reject, signal: options.signal, release: null };
        entry.consumers.add(consumer);
        const release = () => {
            if (!active) return;
            active = false;
            entry.consumers.delete(consumer);
            options.signal?.removeEventListener("abort", release);
            queueMicrotask(() => reject(abortError()));
            if (entry.consumers.size === 0) {
                if (inFlight.get(key) === entry) inFlight.delete(key);
                entry.controller.abort();
            }
        };
        consumer.release = release;
        if (options.signal) {
            if (options.signal.aborted) release();
            else options.signal.addEventListener("abort", release, { once: true });
        }
        });
        result.catch(() => {});
        return result;
    };
};
